import { useId } from 'react';
import styles from './Spinner.module.css';

interface SpinnerProps {
  hasWrapper?: boolean;
  /** Height in px; the watch is about half as wide as it is tall. Default 44. */
  size?: number;
  /** Grain texture — worth it at large sizes only (the full-page loader). */
  grain?: boolean;
}

/**
 * The Beatly watch: a violet strap, a lime → deep purple → teal face and two pink hands that turn while
 * something loads (the long hand sweeps, the short one follows slowly). Drawn in SVG so the hands move on
 * their own; the static artwork is the brand illustration.
 */
export function Watch({ size = 44, grain = false }: { size?: number; grain?: boolean }) {
  const id = useId().replace(/:/g, '');
  const strap = `strap-${id}`, face = `face-${id}`, shade = `shade-${id}`, hand = `hand-${id}`, noise = `noise-${id}`;
  const fade = `fade-${id}`, strapMask = `strapmask-${id}`, faceClip = `faceclip-${id}`;
  return (
    <svg
      className={styles.watch}
      width={Math.round(size * 0.46)}
      height={size}
      viewBox="0 0 200 434"
      role="img"
      aria-label="Loading"
    >
      <defs>
        <linearGradient id={strap} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#f7a8ff" />
          <stop offset="0.3" stopColor="#a445f7" />
          <stop offset="0.7" stopColor="#9b3cf5" />
          <stop offset="1" stopColor="#f39cff" />
        </linearGradient>
        <linearGradient id={face} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#c8f22f" />
          <stop offset="0.42" stopColor="#b9e82c" />
          <stop offset="0.62" stopColor="#7cc24a" />
          <stop offset="0.8" stopColor="#2fae93" />
          <stop offset="1" stopColor="#3cc9a8" />
        </linearGradient>
        <radialGradient id={shade} cx="0.62" cy="0.02" r="0.72">
          <stop offset="0" stopColor="#2a0d3d" stopOpacity="0.95" />
          <stop offset="1" stopColor="#2a0d3d" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={hand} gradientUnits="userSpaceOnUse" x1="40" y1="280" x2="160" y2="160">{/* fixed units: a horizontal hand has a zero-height box */}
          <stop offset="0" stopColor="#fbe7ff" />
          <stop offset="1" stopColor="#f39cff" />
        </linearGradient>
        {grain && (
          /* Large watch only: the strap shows faintly through the top of the face (only where the strap is) */
          <>
            <clipPath id={faceClip}><circle cx="100" cy="217" r="99" /></clipPath>
            <linearGradient id={fade} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#fff" stopOpacity="0.5" />
              <stop offset="0.3" stopColor="#fff" stopOpacity="0.22" />
              <stop offset="0.55" stopColor="#fff" stopOpacity="0" />
            </linearGradient>
            <mask id={strapMask} maskUnits="userSpaceOnUse" x="44" y="118" width="112" height="198">
              <rect x="44" y="118" width="112" height="198" fill={`url(#${fade})`} />
            </mask>
          </>
        )}
        {grain && (
          <filter id={noise} x="0" y="0" width="100%" height="100%">
            <feTurbulence type="fractalNoise" baseFrequency="1.1" numOctaves="2" stitchTiles="stitch" result="n" />
            <feColorMatrix in="n" type="saturate" values="0" result="g" />
            <feComponentTransfer in="g" result="ga"><feFuncA type="linear" slope="0.45" /></feComponentTransfer>
            <feComposite in="ga" in2="SourceGraphic" operator="in" result="gm" />
            <feBlend in="SourceGraphic" in2="gm" mode="multiply" />
          </filter>
        )}
      </defs>
      <g filter={grain ? `url(#${noise})` : undefined}>
        <rect x="44" y="0" width="112" height="434" fill={`url(#${strap})`} />
        <circle cx="100" cy="217" r="99" fill={`url(#${face})`} />
        <circle cx="100" cy="217" r="99" fill={`url(#${shade})`} />
        {grain && (
          <rect x="44" y="118" width="112" height="198" fill={`url(#${strap})`} clipPath={`url(#${faceClip})`} mask={`url(#${strapMask})`} />
        )}
      </g>
      {/* hands pivot on the centre of the face */}
      <g className={styles.handShort}>
        <line x1="100" y1="217" x2="146" y2="217" stroke={`url(#${hand})`} strokeWidth="13" strokeLinecap="round" />
      </g>
      <g className={styles.handLong}>
        <line x1="100" y1="217" x2="50" y2="264" stroke={`url(#${hand})`} strokeWidth="13" strokeLinecap="round" />
      </g>
      <circle cx="100" cy="217" r="6.5" fill="#fbe7ff" />
    </svg>
  );
}

export function Spinner({ hasWrapper = false, size, grain }: SpinnerProps) {
  const watch = <Watch size={size} grain={grain} />;
  if (hasWrapper) return <div className={styles.spinnerWrapper}>{watch}</div>;
  return watch;
}
